import { memo, useMemo, useCallback } from 'react';
import { getCategoryConfig } from '../config/categoryConfigs';

/**
 * CategoryCard Component
 * Displays category information with navigation and theming support
 */
export const CategoryCard = memo(({ 
  categoryType, 
  onNavigate, 
  className = '',
  showDescription = true,
  showFeatures = false 
}) => {
  const config = useMemo(() => getCategoryConfig(categoryType), [categoryType]);
  
  const handleClick = useCallback(() => {
    if (onNavigate && config) {
      onNavigate(`/services/categories/${config.name}`);
    }
  }, [onNavigate, config]);

  if (!config) return null;

  return (
    <div 
      className={`category-card cursor-pointer transition-all duration-300 hover:shadow-lg ${className}`}
      onClick={handleClick}
      style={{
        '--primary-color': config.getCustomization('primaryColor'),
        '--secondary-color': config.getCustomization('secondaryColor')
      }}
    >
      <div className="category-card__header">
        <div className="category-card__icon text-3xl mb-2">
          {config.icon}
        </div>
        <h3 className="category-card__title font-semibold text-lg">
          {config.displayName}
        </h3>
      </div>
      
      {showDescription && (
        <div className="category-card__description text-sm text-gray-600 mt-2">
          {config.description}
        </div>
      )}
      
      {showFeatures && (
        <div className="category-card__features mt-3">
          <div className="flex flex-wrap gap-1">
            {config.features.slice(0, 3).map(feature => (
              <span 
                key={feature}
                className="text-xs px-2 py-1 bg-gray-100 rounded-full"
              >
                {feature.replace(/_/g, ' ').toLowerCase()}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

CategoryCard.displayName = 'CategoryCard';

export default CategoryCard;
